import { useCallback, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import {
	type AccountFormValues,
	createDefaultOwner,
	type IndividualFormData,
	type IndividualGroup,
	type OwnerFormData,
	type SpouseFormData,
} from "../schema";

const FIELD_PATH = "account.details.individuals" as const;

const isOwner = (ind: IndividualFormData | null | undefined): ind is OwnerFormData =>
	ind != null && "type" in ind && ind.type === "owner";

export function useAccountIndividuals() {
	const { control, setValue: _setValue } = useFormContext<AccountFormValues>();
	const setValue = _setValue as (
		path: typeof FIELD_PATH,
		value: IndividualGroup[],
		options?: { shouldValidate?: boolean; shouldDirty?: boolean },
	) => void;
	const groups = (useWatch({ control, name: FIELD_PATH }) ?? []) as IndividualGroup[];

	const [editingIndex, setEditingIndex] = useState<number | null>(null);
	const [editingMemberIndex, setEditingMemberIndex] = useState<number>(0);

	const addOwner = useCallback(() => {
		setEditingMemberIndex(0);
		setEditingIndex(-1);
	}, []);

	const editOwner = useCallback(
		(groupIndex: number) => {
			const group = groups[groupIndex];
			const ownerIdx = group?.findIndex(isOwner) ?? 0;
			setEditingMemberIndex(ownerIdx >= 0 ? ownerIdx : 0);
			setEditingIndex(groupIndex);
		},
		[groups],
	);

	const editSpouseOwner = useCallback(
		(groupIndex: number) => {
			const group = groups[groupIndex];
			const ownerIdx = group?.findIndex(isOwner) ?? 0;
			const spouseIdx = ownerIdx === 0 ? 1 : 0;
			setEditingMemberIndex(spouseIdx);
			setEditingIndex(groupIndex);
		},
		[groups],
	);

	const removeIndividual = useCallback(
		(groupIndex: number, memberIndex: number) => {
			const group = groups[groupIndex];
			if (!group) return;

			const remaining = group.filter((_, i) => i !== memberIndex);
			if (remaining.length > 0 && remaining.some(isOwner)) {
				const updated = groups.map((g, i) => (i === groupIndex ? remaining : g));
				setValue(FIELD_PATH, updated, { shouldValidate: true, shouldDirty: true });
			} else {
				const updated = groups.filter((_, i) => i !== groupIndex);
				setValue(FIELD_PATH, updated, { shouldValidate: true, shouldDirty: true });
			}
		},
		[groups, setValue],
	);

	const saveOwnerAndSpouse = useCallback(
		(owner: OwnerFormData, spouse: IndividualFormData | null) => {
			let group: IndividualGroup;

			if (editingIndex !== null && editingIndex >= 0) {
				const otherIndex = editingMemberIndex === 0 ? 1 : 0;
				const existing = [...groups[editingIndex]];
				existing[editingMemberIndex] = owner;
				if (spouse != null) {
					existing[otherIndex] = spouse;
					group = existing;
				} else {
					group = [owner];
				}
			} else {
				group = spouse != null ? [owner, spouse] : [owner];
			}

			const ownsAHome = owner.ownsAHome;
			group = group.map((member) => (isOwner(member) ? { ...member, ownsAHome } : member));

			let updated: IndividualGroup[];

			if (editingIndex !== null && editingIndex >= 0) {
				updated = groups.map((g, i) => (i === editingIndex ? group : g));
			} else {
				updated = [...groups, group];
			}

			setValue(FIELD_PATH, updated, { shouldValidate: true, shouldDirty: true });
			setEditingIndex(null);
			setEditingMemberIndex(0);
		},
		[groups, setValue, editingIndex, editingMemberIndex],
	);

	const setCreditPermission = useCallback(
		(groupIndex: number, memberIndex: number, value: boolean | undefined) => {
			const group = groups[groupIndex];
			if (!group) return;

			const target = group[memberIndex];
			if (!target || !isOwner(target)) return;

			const updatedGroup = group.map((member, i) =>
				i === memberIndex ? { ...member, creditReportPermission: value } : member,
			);

			const updated = groups.map((g, i) => (i === groupIndex ? updatedGroup : g));
			setValue(FIELD_PATH, updated, { shouldValidate: true, shouldDirty: true });
		},
		[groups, setValue],
	);

	const close = useCallback(() => {
		setEditingIndex(null);
		setEditingMemberIndex(0);
	}, []);

	const getEditingOwner = (): Partial<OwnerFormData> | undefined => {
		if (editingIndex === null) return undefined;
		if (editingIndex === -1) return createDefaultOwner();
		const group = groups[editingIndex];
		return group?.[editingMemberIndex] as OwnerFormData;
	};

	const getEditingSpouse = (): Partial<SpouseFormData> | undefined => {
		if (editingIndex === null) return undefined;
		if (editingIndex < 0) return undefined;
		const group = groups[editingIndex];
		const otherIndex = editingMemberIndex === 0 ? 1 : 0;
		return (group?.[otherIndex] ?? undefined) as Partial<SpouseFormData> | undefined;
	};

	const entries = groups.map((group, index) => {
		const ownerIdx = group.findIndex(isOwner);
		const owner = ownerIdx >= 0 ? (group[ownerIdx] as OwnerFormData) : (group[0] as OwnerFormData);
		const spouse =
			ownerIdx >= 0
				? (group.find((_, i) => i !== ownerIdx) as IndividualFormData | undefined)
				: undefined;
		return { owner, spouse, index };
	});

	return {
		entries,
		isOpen: editingIndex !== null,
		editingOwner: getEditingOwner(),
		editingSpouse: getEditingSpouse(),
		addOwner,
		editOwner,
		editSpouseOwner,
		removeIndividual,
		saveOwnerAndSpouse,
		setCreditPermission,
		close,
	};
}
